import { useEffect, useRef, useState } from "react";
import { CirclePlus, LibraryBig, Loader2, SearchIcon } from "lucide-react";
import { Input } from "@ui/input";
import { translate } from "@modules/localizations/translate";
import { useDebounce } from "@utils/hooks/useDebounce";
import { useInfiniteScroll } from "@utils/hooks/useInfiniteScroll";
import { useSearchTracked } from "@lib/stores/content/hooks";
import {
  addItemToPlaylist,
  clearSearchTracked,
  searchTracked,
  useAppDispatch,
  useAppSelector,
} from "@lib/stores";
import { Skeleton } from "@ui/skeleton";
import TranslatedText from "@components/items/display/TranslatedText";
import { db } from "@modules/local/db";
import { useAppLiveQuery } from "@modules/local/hooks";
import { ComponentsActions } from "@lib/fetchers/components";
import { getComponentParameterValues } from "@modules/content/helpers/content";
import { HorizontalListItemParameters } from "@components/layouts/content/HorizontalList";
import { useSearchParams } from "react-router-dom";
import { ContentActions } from "@lib/stores/content/slices";
import { Badge } from "@ui/badge";
import { getPlaylistByIdAsync } from "@lib/stores/myLibrary/slices";

export const SearchInput = ({
  setOpenAddItemDialog,
}: {
  setOpenAddItemDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchParams] = useSearchParams();
  const currentPlaylistId = searchParams.get("id");

  const dispatch = useAppDispatch();

  const [searchTerm, setSearchTerm] = useState<string>();
  const [pageNumber, setPageNumber] = useState(1);

  const userId = useAppSelector((state) => state.auth.user.id ?? "");

  const { result: flow } = useAppLiveQuery(
    () => db.readerFlows.get(userId),
    [userId]
  );

  const { result: searchComponent } = useAppLiveQuery(
    () => db.components.get(flow?.searchAllComponentId || ""),
    [flow?.searchAllComponentId]
  );

  const { data, status, meta } = useSearchTracked();

  const [setTermDebounced] = useDebounce((term) => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    clearSearchTracked();

    setPageNumber(1);
    setSearchTerm(term as string);
  });

  const { lastElementRef } = useInfiniteScroll(meta.hasNextPage, setPageNumber);

  const clear = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    clearSearchTracked();

    setSearchTerm(undefined);
  };

  useEffect(() => {
    const listener = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        wrapperRef.current &&
        popoverRef.current &&
        !(
          wrapperRef.current.contains(target) ||
          popoverRef.current.contains(target)
        )
      ) {
      }
    };

    document.body.addEventListener("click", listener);

    return () => {
      document.body.removeEventListener("click", listener);
    };
  }, []);

  useEffect(() => {
    if (searchTerm) {
      searchTracked({
        searchText: searchTerm,
        pageNumber,
        pageSize: 10,
        // typeName: "BOOK",
      });
    }
  }, [searchTerm, pageNumber]);

  const getComponentAsync = async (id: string | undefined) => {
    if (!id) return;

    await ComponentsActions.getComponentByIdAsync(id);
  };

  useEffect(() => {
    getComponentAsync(flow?.searchAllComponentId);
  }, [flow?.searchAllComponentId]);

  const renderResult = () => {
    return getComponentParameterValues<HorizontalListItemParameters>(
      ["image"],
      searchComponent,
      data
    ).map((d, i) => {
      return (
        <div
          key={d.content.id}
          ref={data.length - 1 === i ? lastElementRef : null}
          className="flex cursor-pointer items-center justify-start gap-x-2 p-5 hover:bg-blue-50 dark:hover:text-black"
          onClick={() => {
            const input = {
              playlistId: parseInt(currentPlaylistId ?? ""),
              contentEntityId: d.content.id,
            };
            setOpenAddItemDialog(false);
            addItemToPlaylist(input, () => {
              dispatch(getPlaylistByIdAsync(parseInt(currentPlaylistId ?? "")));
              dispatch(
                ContentActions.getContentEntityItemsByPlaylistIdAsync(
                  parseInt(currentPlaylistId ?? "")
                )
              );
            });
            clear();
          }}
        >
          <div className="h-20 w-20 min-w-20 overflow-hidden rounded-lg">
            <img
              src={d.values.image || ""}
              alt={d.content.title}
              className="h-full w-full"
            />
          </div>
          <div className="flex w-full justify-between">
            <div className="flex flex-col justify-center gap-1.5">
              <div className="max-w-[200px] text-[13px] font-medium">
                {d.content.title}
              </div>
              <div className="text-[11px] text-[#818181]">
                {d.content?.authors?.[0]}
              </div>
              <Badge className="w-fit rounded-md p-1 text-[11px] font-medium text-white">
                {d.content.typeDisplayName}
              </Badge>
            </div>
            <CirclePlus
              className="fill-[#D9D9D9] text-white dark:fill-[#3C3B40]"
              size={30}
            />
          </div>
        </div>
      );
    });
  };

  return (
    <div
      ref={wrapperRef}
      className="flex w-[400px] flex-col items-center justify-center"
    >
      <div className="relative h-[41px] w-full">
        <Input
          ref={inputRef}
          className="placeholder:text[16px] w-full self-center rounded-full border-none bg-[#E7E7E7] px-10 text-black focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-[#898A8D70] dark:text-white"
          placeholder={translate(`layout.b2c-search-placeholder`)}
          onChange={(event) => {
            const value = event.target.value;

            setTermDebounced(value);

            if (!value) {
              clearSearchTracked();

              setSearchTerm(undefined);
            }
          }}
        />
        <div className="absolute top-[20%] px-3">
          <SearchIcon className="text-[#cfcfcf]" />
        </div>
      </div>
      <div className="cus-scrollbar-w-none h-[400px] w-full overflow-y-auto text-black dark:text-white">
        {data.length ? (
          <div className="flex h-full w-full flex-col gap-y-2">
            {renderResult()}
            {status === "loading" ? (
              <div className="flex h-12 w-full items-center justify-center">
                <Loader2 className={`animate-spin`} />
              </div>
            ) : null}
          </div>
        ) : status === "loading" ? (
          <SkeletonLoading />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-5">
            <TranslatedText className="text-[20px]" text="layout.search" />
            <LibraryBig
              size={100}
              className="fill-primary stroke-[1.5px] dark:stroke-gray-300"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const SkeletonLoading = () => {
  const skeletonData: { id: string }[] = Array.from({ length: 10 }).map(
    (s, i) => {
      return {
        id: i + "",
      };
    }
  );

  const renderItems = () => {
    return skeletonData.map((s) => {
      return (
        <div key={s.id} className="flex items-center justify-start gap-x-2">
          <Skeleton
            className={`h-12 w-12 min-w-12 rounded-sm bg-[#f4f4f5] dark:bg-[#27272a]`}
          ></Skeleton>
          <Skeleton
            className={`h-5 w-full rounded-sm bg-[#f4f4f5] dark:bg-[#27272a]`}
          ></Skeleton>
        </div>
      );
    });
  };

  return <div className="flex flex-col gap-y-2">{renderItems()}</div>;
};
